import { useRouter } from 'found';
import React from 'react';
import {
  createPaginationContainer,
  graphql,
  Environment,
  RefetchOptions,
} from 'react-relay';
import styled from 'styled-components';
import { ImageIcon } from '../../components/ImageIcon';
import Button from '../../components/input/Button';
import FlatButton from '../../components/input/FlatButton';
import Spacer from '../../components/Spacer';
import Table from '../../components/Table/Table';
import {
  PageTitle,
  TitleContainer,
} from '../../components/typography/Typography';
import { ApiList_account } from './__generated__/ApiList_account.graphql';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import { format } from 'date-fns';
import { routes } from '../../routes';
import { formatCurrency } from '../../helpers';

const Container = styled.div`
  // width: 1200px;
  flex: 1;
`;

const NothingHere = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NothingTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const NothingSubtitle = styled.div`
  font-size: 13px;
  color: #9092c1;
`;

const NothingImage = styled.img``;

type Props = {
  account: ApiList_account;
  relay: {
    environment: Environment;
    hasMore: () => boolean;
    isLoading: () => boolean;
    loadMore: (
      pageSize: number,
      callback: (error: Error) => void,
      options?: RefetchOptions
    ) => any;
  };
};

function ApiList({ account, relay }: Props) {
  const { router } = useRouter();
  const { hasMore, isLoading, loadMore } = relay;

  const dispatch = useToastDispatch();

  const services = account.services.edges;
  console.log('services', services);

  return (
    <Container>
      <Spacer spacing={4} vertical />
      <TitleContainer>
        <PageTitle style={{ flex: 1 }}>APIs</PageTitle>
        <Button
          color={'white'}
          onClick={() => {
            router.push(routes.createAPI);
          }}
        >
          New API
        </Button>
      </TitleContainer>
      <Spacer vertical spacing={4} />
      {services && services.length > 0 ? (
        <Table
          rows={services?.map((service) => service?.node) ?? []}
          columns={[
            {
              headerName: 'Name',
              component: (row) => (
                <ImageIcon imageURL={row.imageURL} title={row.name} />
              ),
              flex: 1,
            },
            {
              headerName: 'Created at',
              component: (row) => (
                <div>{format(new Date(row.createdAt), 'MM/dd/yyyy')}</div>
              ),
              width: 200,
            },
            {
              headerName: 'This Month (revenue)',
              component: (row) => (
                <div>
                  {row.usage?.totalCosts.map((cost: any) => (
                    <>
                      {formatCurrency(cost.currency, cost.value)}
                      <Spacer horizontal spacing={1} />
                    </>
                  ))}
                </div>
              ),
              width: 200,
            },
          ]}
          onRowClick={(row) => {
            router.push(`${routes.api}/${row.id}`);
          }}
        />
      ) : (
        <NothingHere>
          <Spacer vertical spacing={3} />
          <NothingTitle>No apis yet</NothingTitle>
          <Spacer vertical spacing={1} />
          <NothingSubtitle>Create one, or just enjoy this blob</NothingSubtitle>
          <Spacer vertical spacing={4} />
          <NothingImage src={'/images/noapis.svg'} />
        </NothingHere>
      )}

      <Spacer vertical spacing={4} />
      {hasMore() && (
        <>
          <FlatButton
            darkenOnHover
            color='#171C3C'
            background='#e9e9ee'
            onClick={() => {
              loadMore(10, (error) => {
                if (error) {
                  console.log('err', error);
                  dispatch({
                    alert: 'error',
                    text: 'Unable to load more APIs, please try again',
                  });
                }
              });
            }}
          >
            {isLoading() ? 'Loading...' : 'Load More'}
          </FlatButton>
          <Spacer vertical spacing={4} />
        </>
      )}
    </Container>
  );
}

export default createPaginationContainer(
  ApiList,
  {
    account: graphql`
      fragment ApiList_account on Account
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 10 }
          cursor: { type: "String" }
          before: { type: "Time!" }
          after: { type: "Time!" }
        ) {
        services(first: $count, after: $cursor)
          @connection(key: "ApiList_services") {
          edges {
            node {
              id
              name
              imageURL
              createdAt
              usage(before: $before, after: $after) {
                totalCosts {
                  currency
                  value
                }
              }
            }
          }
        }
      }
    `,
  },
  {
    getConnectionFromProps(props: any) {
      return props.account && props.account.services;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
        // userID isn't specified as an @argument for the fragment, but it should be a variable available for the fragment under the query root.
        accountID: fragmentVariables.accountID,
      };
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query ApiListPaginationQuery(
        $count: Int!
        $cursor: String
        $accountID: ID!
        $before: Time!
        $after: Time!
      ) {
        account: node(id: $accountID) {
          ...ApiList_account
            @arguments(
              count: $count
              cursor: $cursor
              before: $before
              after: $after
            )
        }
      }
    `,
  }
);
